/**
 * ============================================================================
 * connect-counsellor — shared backup trigger
 * ============================================================================
 * Single canonical implementation of the `/api/connect-counsellor` fire-and-
 * forget backup call, used by every form that creates a Salesforce lead via
 * `/api/salesforce.com/lead-create-new` or `/api/salesforce.com/lead-create-lp-new`.
 *
 * Contract (unchanged from the pre-centralization per-form copies):
 *   POST /api/connect-counsellor
 *   body: { ipstack, form, meta, status }
 *
 * Non-blocking by design: callers must NOT `await` this. It fires the request
 * and returns immediately; failures are caught and logged here so a slow or
 * failing backup call can never delay OTP dispatch, lead creation, or modal
 * progression, and never produces an unhandled promise rejection.
 * ============================================================================
 */
import { getIpJSONData } from "@/context/ip.storage.utils";

export interface ConnectCounsellorPayload {
  form: Record<string, any>;
  meta: Record<string, any>;
  status: boolean;
  /** Optional webinar/zoom-call extras — only a small number of callers use these. */
  isWebinar?: boolean;
  zoomApiCallResponse?: any;
  ApiRequest?: any;
  ZoomStatus?: any;
}

/**
 * Fires the connect-counsellor backup request. Fire-and-forget: does not
 * return a promise for the caller to await, and never throws.
 */
export function connectCounsellor({
  form,
  meta,
  status,
  isWebinar,
  zoomApiCallResponse,
  ApiRequest,
  ZoomStatus,
}: ConnectCounsellorPayload): void {
  void fetch("/api/connect-counsellor", {
    method: "POST",
    body: JSON.stringify({
      ipstack: getIpJSONData(),
      form,
      meta,
      status,
      ...(isWebinar !== undefined ? { isWebinar } : {}),
      ...(zoomApiCallResponse !== undefined ? { zoomApiCallResponse } : {}),
      ...(ApiRequest !== undefined ? { ApiRequest } : {}),
      ...(ZoomStatus !== undefined ? { ZoomStatus } : {}),
    }),
  }).catch((err) => {
    console.error("Error in connect-counsellor backup call:", err);
  });
}

const LEAD_CREATE_ENDPOINTS = [
  "/api/salesforce.com/lead-create-new",
  "/api/salesforce.com/lead-create-lp-new",
] as const;

function matchesLeadCreateEndpoint(url: string): boolean {
  return LEAD_CREATE_ENDPOINTS.some(
    (endpoint) => url === endpoint || url.endsWith(endpoint),
  );
}

/**
 * Drop-in replacement for `fetch`, used by every form's own lead-create
 * service function in place of a raw `fetch(...)` call.
 *
 * When the request targets /api/salesforce.com/lead-create-new or
 * /api/salesforce.com/lead-create-lp-new, it automatically fires the
 * connect-counsellor backup (fire-and-forget — see connectCounsellor() above)
 * once the lead-create response is known, success or failure, using the
 * same `form`/`meta` that were actually sent in that request body. Forms no
 * longer need their own explicit `connectCounsellor(...)` call — swapping
 * `fetch` for `leadCreateFetch` inside the service function is enough.
 *
 * The returned Response is untouched (a clone is read internally), so every
 * existing call site keeps working exactly as before — same return value,
 * same timing, no extra latency: the backup lookup runs independently and
 * is never awaited before this function returns.
 *
 * Do NOT use this for anything that isn't a form-level initial lead create.
 * In particular, OTPContext's post-verification lead-update calls
 * (leadUpdateForLeadCreate / leadUpdateForLeadCreateLP in
 * otpContext.service.ts) hit these same URLs but must NOT trigger the
 * backup — those keep using plain `fetch`.
 */
export function leadCreateFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const url = typeof input === "string" ? input : input.toString();
  const responsePromise = fetch(input, init);

  if (!matchesLeadCreateEndpoint(url)) {
    return responsePromise;
  }

  let parsedBody: { form?: Record<string, any>; meta?: Record<string, any> } | null =
    null;
  try {
    if (typeof init?.body === "string") {
      parsedBody = JSON.parse(init.body);
    }
  } catch {
    parsedBody = null;
  }

  if (parsedBody?.form) {
    const { form } = parsedBody;
    const meta = parsedBody.meta || {};
    responsePromise
      .then((response) => response.clone().json())
      .then((result) => {
        connectCounsellor({ form, meta, status: !!result?.success });
      })
      .catch(() => {
        connectCounsellor({ form, meta, status: false });
      });
  }

  return responsePromise;
}
